import React, { useEffect, useMemo, useRef, useState } from 'react';
import ClientPricingHistoryService from '../../../../Services/clientPricingHistoryService';
import ClientPricingHistoryItem from '../../../../Domain/ClientPricingHistoryItem';
import ClientPricingHistoryTotalItem from '../../../../Domain/ClientPricingHistoryTotalItem';
import ColumnsMenu from '../../../../Elements/ColumnsMenu';
import DataTable from '../../../../Elements/DataTable/DataTableFigma';
import ErrorMessage from '../../../../Elements/ErrorMessage';
import Loading from '../../../../Elements/loading';
import GeneralSearchPanel from '../../../../Elements/SearchPanel/GeneralSearchPanelFigma';
import SegmentedToggle from '../../../../Elements/SegmentedToggle';
import OrderDirectionsEnum from '../../../../Enums/OrderDirectionsEnum';
import columnVisibilityHelper from '../../../../Helpers/columnVisibilityHelper';
import {
  CURRENCY_NATIVE,
  CURRENCY_OPTIONS,
  DEFAULT_SORT,
  DEFAULT_SPLIT_BY,
  FILTER_FIELDS,
} from './clientPricingHistoryConstants';
import {
  activeFilterCount,
  childClientsText,
  clearPanelSettings,
  defaultSettings,
  hiddenColumnKeys,
  isDimension,
  nextSort,
  pruneFilters,
  sameSelection,
  selectionText,
  toggleKey,
  toRequest,
  toSettings,
} from './clientPricingHistoryHelpers';
import ClientPricingHistoryNothingInvoiced from './ClientPricingHistoryNothingInvoiced';
import { searchParamsTypeFor } from './clientPricingHistorySearchParams';
import type { FilterOptions, HistoryData, Settings, SortState } from './clientPricingHistoryTypes';

// The Client page's "Pricing History" tab (CD-5650): for each year, the number of invoiced evaluations and the
// client fee, optionally split by client, industry, service type, fieldwork office and country. The server
// (ClientPricingHistory_Micro) does all the maths; this component only collects the settings and shows the answer.

interface ClientPricingHistoryTabProps {
  clientId: number;
}

const ClientPricingHistoryTab = (props: ClientPricingHistoryTabProps) => {
  const { clientId } = props;

  const [settings, setSettings] = useState<Settings>(defaultSettings);
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT);
  // The amount columns the user hid. Unlike a field, hiding one changes nothing on the server.
  const [hiddenMeasures, setHiddenMeasures] = useState<string[]>([]);

  const [data, setData] = useState<HistoryData | null>(null);
  // The settings the data shown was fetched for; its filter lists belong to exactly that selection.
  const [dataFor, setDataFor] = useState<Settings | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // While the Filters panel is open: the selection made in it, the filter values the panel offers, and the selection
  // those values were worked out for (null when that is not known).
  const [panelOpen, setPanelOpen] = useState<boolean>(false);
  const [draft, setDraft] = useState<Settings | null>(null);
  const [draftOptions, setDraftOptions] = useState<FilterOptions | null>(null);
  const [optionsFor, setOptionsFor] = useState<Settings | null>(null);

  const dataTicket = useRef<number>(0);
  const optionsTicket = useRef<number>(0);

  // Everything the server needs to answer. It only changes when a setting changes, so it drives the fetch.
  const request = useMemo(() => toRequest(clientId, settings, sort), [clientId, settings, sort]);

  useEffect(() => {
    const ticket = dataTicket.current + 1;
    dataTicket.current = ticket;
    const requested = settings;
    setLoading(true);
    setError(null);
    ClientPricingHistoryService.GetPricingHistory(request)
      .then((result: HistoryData) => {
        if (ticket !== dataTicket.current) return;
        setData(result);
        setDataFor(requested);
        setLoading(false);
      })
      .catch((e: any) => {
        if (ticket !== dataTicket.current) return;
        setLoading(false);
        setError('The pricing history could not be loaded.');
        ErrorMessage.SetErrorMessage('The pricing history could not be loaded.', e);
      });
  }, [request]);

  // While the Filters panel is open, keep its lists narrowed by what is ticked in it (nothing is applied yet). The lists
  // it already shows are kept when they belong to this very selection, as on opening it; a request still on its way
  // for an earlier selection is then ignored.
  useEffect(() => {
    if (!draft) return;
    const ticket = optionsTicket.current + 1;
    optionsTicket.current = ticket;
    if (optionsFor && sameSelection(optionsFor, draft)) return;
    ClientPricingHistoryService.GetFilterOptions(toRequest(clientId, draft, DEFAULT_SORT))
      .then((options: FilterOptions) => {
        if (ticket !== optionsTicket.current) return;
        setDraftOptions(options);
        setOptionsFor(draft);
      })
      .catch((e: any) => {
        if (ticket !== optionsTicket.current) return;
        ErrorMessage.SetErrorMessage('The filter values could not be loaded.', e);
      });
  }, [clientId, draft]);

  const openPanel = () => {
    setDraft(null);
    setDraftOptions(data ? data.options : null);
    setOptionsFor(data ? dataFor : null);
    setPanelOpen(true);
  };

  const closePanel = () => setPanelOpen(false);

  // A ticked value the narrowed lists no longer offer is dropped rather than applied. The lists have to be the ones
  // for exactly this selection; when the panel's last answer was for an earlier one, the right lists are asked first.
  const applySearchItem = (searchItem: any) => {
    const applied = toSettings(searchItem);
    if (draftOptions && optionsFor && sameSelection(optionsFor, applied)) {
      setSettings(pruneFilters(applied, draftOptions));
      return;
    }
    ClientPricingHistoryService.GetFilterOptions(toRequest(clientId, applied, DEFAULT_SORT))
      .then((options: FilterOptions) => setSettings(pruneFilters(applied, options)))
      .catch(() => setSettings(applied));
  };

  // The toolbar's eraser: everything back to the opening view.
  const resetAll = () => {
    setSettings(defaultSettings());
    setSort(DEFAULT_SORT);
    setHiddenMeasures([]);
    setPanelOpen(false);
  };

  // The Filters panel's Clear all: only what the panel holds, applied at once.
  const clearPanel = () => {
    setSettings((previous) => clearPanelSettings(previous));
    setPanelOpen(false);
  };

  const onSort = (sortKey: string) => setSort((previous) => nextSort(previous, sortKey));

  const setCurrency = (currency: string) => setSettings((previous) => ({ ...previous, Currency: currency }));

  // Ticking a field splits the rows by it (a new request); an amount column is only shown or hidden.
  const toggleColumn = (columnKey: string) => {
    if (isDimension(columnKey)) {
      setSettings((previous) => ({ ...previous, SplitBy: toggleKey(previous.SplitBy, columnKey) }));
    } else {
      setHiddenMeasures((previous) => toggleKey(previous, columnKey));
    }
  };

  const resetColumns = () => {
    setSettings((previous) => ({ ...previous, SplitBy: [...DEFAULT_SPLIT_BY] }));
    setHiddenMeasures([]);
  };

  // Year, the fields and the amounts, as the grid declares them; Year is locked on.
  const columns = useMemo(() => columnVisibilityHelper.getSelectableColumns(ClientPricingHistoryItem), []);
  const hiddenColumns = useMemo(() => hiddenColumnKeys(settings.SplitBy, hiddenMeasures), [settings, hiddenMeasures]);
  const rows = useMemo(() => (data ? ClientPricingHistoryService.ConvertDBItems(data.rows) : []), [data]);
  const totals = useMemo(() => (data ? ClientPricingHistoryService.ConvertDBTotalItems(data.totals) : []), [data]);

  const childCount = data ? data.childClientCount : 0;
  const filterCount = activeFilterCount(settings);

  const noRowsMessage =
    !settings.IncludeChildClients && childCount > 0
      ? 'No invoiced evaluations match these settings. Include the child clients in Filters to see theirs.'
      : 'No invoiced evaluations match these settings.';
  // Nothing at all is invoiced yet, as opposed to a filter or the child clients switch leaving nothing. Judged by the
  // settings the data was fetched for, so a filtered empty answer is not taken for it while a reset is on its way.
  const nothingInvoiced =
    !!data &&
    !!dataFor &&
    data.rows.length === 0 &&
    activeFilterCount(dataFor) === 0 &&
    (dataFor.IncludeChildClients || childCount === 0);

  // The two controls used most come first. Chips follow for what is set in the Filters panel: whether a parent's child
  // clients are in, and the applied filters. The client itself is in the detail's title.
  const renderToolbar = () => (
    <div className='toolbar'>
      <SegmentedToggle
        options={CURRENCY_OPTIONS}
        value={settings.Currency}
        onChange={setCurrency}
      />
      <ColumnsMenu
        columns={columns}
        hiddenColumns={hiddenColumns}
        onToggleColumn={toggleColumn}
        onResetColumns={resetColumns}
      />
      <div className='cph-context'>
        {childCount > 0 && (
          <span className='chip'>
            {settings.IncludeChildClients ? 'Includes' : 'Excludes'} <b>{childClientsText(childCount)}</b>
          </span>
        )}
        {FILTER_FIELDS.filter(({ key }) => settings[key].length > 0).map(({ key, label }) => (
          <span
            key={key}
            className='chip'
          >
            {label}: <b>{selectionText(settings[key])}</b>
          </span>
        ))}
      </div>
      <button
        type='button'
        className='btn'
        aria-haspopup='dialog'
        onClick={openPanel}
      >
        <i className='fa-solid fa-sliders' />
        <span className='btn-label'>Filters</span>
        {filterCount > 0 && <span className='fcount'>{filterCount}</span>}
      </button>
      <button
        type='button'
        className='icon-btn clear-filters'
        title='Reset to defaults'
        onClick={resetAll}
      >
        <i className='fa-solid fa-eraser' />
      </button>
    </div>
  );

  const renderResult = () => {
    if (!data) {
      return error ? (
        <DataTable
          itemList={[]}
          type={ClientPricingHistoryItem}
          thereIsNoSuchRowMessage={error}
        />
      ) : (
        <Loading />
      );
    }
    if (nothingInvoiced) {
      return <ClientPricingHistoryNothingInvoiced />;
    }
    return (
      <div
        aria-busy={loading}
        style={loading ? { opacity: 0.6 } : undefined}
      >
        <DataTable
          itemList={rows}
          type={ClientPricingHistoryItem}
          orderBy={sort.key}
          orderDirection={sort.desc ? OrderDirectionsEnum.Desc : OrderDirectionsEnum.Asc}
          changeOrderBy={onSort}
          hiddenColumns={hiddenColumns}
          thereIsNoSuchRowMessage={noRowsMessage}
        />
        {totals.length > 0 && (
          <>
            <div className='ds-label mt-3 mb-1'>Totals</div>
            <DataTable
              itemList={totals}
              type={ClientPricingHistoryTotalItem}
              hiddenColumns={hiddenColumns}
            />
          </>
        )}
      </div>
    );
  };

  return (
    <div className='cph'>
      {renderToolbar()}

      {renderResult()}

      <div className='cph-notes'>
        <span>
          Completed, billable jobs on <strong>Finalized</strong> or <strong>Paid</strong> invoices. Credit memos are not
          netted off.
        </span>
        <span>
          {settings.Currency === CURRENCY_NATIVE
            ? 'Native: amounts are in the currency each fee was invoiced in. Different currencies are never added together.'
            : 'USD: each shop is converted at the rate of its shop date, so a flat contract price can still move with exchange rates.'}
        </span>
        <span>In Columns, ticking a field splits the rows by it, and unticking it adds those rows together.</span>
        <span>
          Fee per Evaluation is the row’s fee divided by its evaluations, so it stays correct when rows are combined.
          Click a column header to sort; a second click reverses and a third returns to the default order.
        </span>
        <span>The current year is year to date, so its volume is partial.</span>
      </div>

      {panelOpen && (
        <GeneralSearchPanel
          searchItem={settings}
          searchparamsType={searchParamsTypeFor(childCount, draftOptions)}
          clickOnOk={(type: string, searchItem: any) => applySearchItem(searchItem)}
          clickOnCancel={closePanel}
          hideFilter={closePanel}
          clickOnClearAll={clearPanel}
          enableUrlPush={false}
          changeOfSearchItemFunction={(searchItem: any) => setDraft(toSettings(searchItem))}
        />
      )}
    </div>
  );
};

export default ClientPricingHistoryTab;
